import React from 'react';
import { Search } from 'react-feather';
import { cx } from './cx';

interface FieldProps {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  id?: string;
  className?: string;
  /** Render-prop receives the id and aria props to spread on the control. */
  children: (control: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => React.ReactNode;
}

/** Label + control + hint/error. Every form control outside a table goes in a Field. */
export const Field: React.FC<FieldProps> = ({ label, hint, error, required, id, className, children }) => {
  const autoId = React.useId();
  const fieldId = id ?? autoId;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
  return (
    <div className={cx('ds-field', className)}>
      <label className="ds-field__label" htmlFor={fieldId}>
        {label}
        {required && <span className="ds-field__required">REQUIRED</span>}
      </label>
      {children({ id: fieldId, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {error ? (
        <span id={`${fieldId}-error`} className="ds-field__error">{error}</span>
      ) : (
        hint && <span id={`${fieldId}-hint`} className="ds-field__hint">{hint}</span>
      )}
    </div>
  );
};

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md';
  numeric?: boolean;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ size = 'md', numeric, className, ...rest }, ref) => (
    <input
      ref={ref}
      className={cx('ds-input', size === 'sm' && 'ds-input--sm', numeric && 'ds-input--numeric', className)}
      inputMode={numeric ? 'decimal' : rest.inputMode}
      {...rest}
    />
  ),
);
TextInput.displayName = 'TextInput';

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(({ className, rows = 3, ...rest }, ref) => (
  <textarea ref={ref} rows={rows} className={cx('ds-input', 'ds-textarea', className)} {...rest} />
));
TextArea.displayName = 'TextArea';

export const SearchInput = React.forwardRef<HTMLInputElement, TextInputProps>((props, ref) => (
  <div className="ds-input-icon">
    <span className="ds-input-icon__icon"><Search size={16} aria-hidden /></span>
    <TextInput ref={ref} type="search" {...props} />
  </div>
));
SearchInput.displayName = 'SearchInput';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  size?: 'sm' | 'md';
}

/** Native select for short static option lists (filters, sort). Use react-select only for search/async. */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, size = 'md', className, ...rest }, ref) => (
    <select ref={ref} className={cx('ds-select', size === 'sm' && 'ds-input--sm', className)} {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  ),
);
Select.displayName = 'Select';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: React.ReactNode;
  hint?: React.ReactNode;
}

/** A checkbox with its label to the right. Clicking the label toggles it. */
export const Checkbox: React.FC<CheckboxProps> = ({ label, hint, className, ...rest }) => (
  <label className={cx('ds-check', className)}>
    <input type="checkbox" {...rest} />
    <span>
      {label}
      {hint && <span className="ds-check__hint">{hint}</span>}
    </span>
  </label>
);

/** Wraps filter controls; give the search field className="ds-toolbar__grow". */
export const Toolbar: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cx('ds-toolbar', className)} role="search">{children}</div>
);
